import { Box, Button, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useCallback, useState } from 'react';
import { DropzoneOptions, FileWithPath, useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';


interface Props {
  config?: DropzoneOptions;
  onFilesUpload: (uploadedFiles: FileWithPath[]) => void;
};


export const DndFileUploader = ({
  config = {},
  onFilesUpload,
}: Props) => {

  const classes = useStyles();

  const [uploadedFiles, setUploadedFiles] = useState<FileWithPath[]>([]);

  const onDrop = useCallback(acceptedFiles => {
    const uploadedFilesToSet = [...uploadedFiles, ...acceptedFiles];
    setUploadedFiles(uploadedFilesToSet);
    onFilesUpload(uploadedFilesToSet);
  }, [uploadedFiles]);

  const defaultConfig = {
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10485760,  // no limitation
    maxFiles: 0,        // 10mb
    noClick: true,
    noKeyboard: true,
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone(
    {
      onDrop,
      ...defaultConfig,
      ...config,
    }
  );

  const bytesToMB = (bytes: number) => {
    try {
      return (bytes / 1048576).toFixed(2);
    } catch (error) {
      console.error(error);
    }
  };

  const removeFile = (file: FileWithPath) => () => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
    onFilesUpload(newFiles);
  };

  return <div {...getRootProps()}>
    <input {...getInputProps()} />
    {!uploadedFiles.length && <Box className={classes.uploadBox}>
      {!isDragActive ? <>
        <CloudUploadIcon sx={{ color: '#2196F3' }} fontSize='large' />
        <Typography>Drag & drop or</Typography>
        <Button variant='text' onClick={open} sx={{ color: '#2196F3' }} >Choose File</Button>
        <Typography variant='body2'>PDF, PNG, JPG — max 10MB</Typography>
      </>
        : <Typography variant='h4'>Drop Here</Typography>}
    </Box>}
    {uploadedFiles.map(file => <Box key={file.path} className={classes.uploadedFile}>
      <Box className={classes.uploadedFileSideBox}>
        <InsertDriveFileIcon className={classes.fileIcon} />
        <Typography variant='body2' lineHeight='normal'>{file.name}</Typography>
      </Box>
      <Box className={classes.uploadedFileSideBox}>
        <Typography variant='body2' lineHeight='normal'>{bytesToMB(file.size)} MB</Typography>
        <IconButton onClick={removeFile(file)} size='small' className={classes.deleteFileIcon}>
          <DeleteOutlineIcon />
        </IconButton>
      </Box>
    </Box>)}
  </div>
};

const useStyles = makeStyles(() => {
  return {
    uploadBox: {
      borderRadius: 8,
      border: '2px dashed rgba(0, 0, 0, 0.12)',
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '& >:not(:last-child)': {
        marginBottom: 4
      }
    },
    uploadedFile: {
      padding: '8px 16px',
      backgroundColor: '#EEF0FA',
      borderRadius: '4px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      '&:not(:last-child)': {
        marginBottom: 8
      }
    },
    uploadedFileSideBox: {
      display: 'flex',
      alignItems: 'center'
    },
    fileIcon: {
      color: '#9FA8DA',
      marginRight: '4px',
    },
    deleteFileIcon: {
      '&.MuiButtonBase-root': {
        color: '#FF5252',
        marginLeft: '4px',
      },
    },
  };
});
